import { Suspense, lazy } from 'react'
import * as Sentry from '@sentry/react'
import { createBrowserRouter } from 'react-router-dom'
import Layout from '../components/Layout'
import RouteFallback from '../components/RouteFallback'

// A stale open tab (or a browser holding a cached index.html) can reference
// chunk hashes that no longer exist after a deploy. The dynamic import then
// rejects ("Failed to fetch dynamically imported module"), the lazy route
// never mounts, and the UI appears dead. Reload once to pull the fresh build;
// a sessionStorage guard prevents a reload loop if the failure is genuine.
const CHUNK_RELOAD_KEY = 'chunk-reload-attempt'

function reloadedOnce(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1'
  } catch {
    return false
  }
}

function lazyWithReload<T extends { default: React.ComponentType<unknown> }>(
  factory: () => Promise<T>,
) {
  return lazy(() =>
    factory()
      .then((mod) => {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY)
        } catch {
          // ignore — private mode
        }
        return mod
      })
      .catch((err) => {
        if (!reloadedOnce()) {
          try {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
          } catch {
            // ignore — private mode
          }
          window.location.reload()
          // Never resolve: the page is reloading, so avoid flashing the
          // error boundary before navigation tears the app down.
          return new Promise<T>(() => {})
        }
        // Second failure in the same session — surface it to the boundary.
        throw err
      }),
  )
}

// Lazy-load each route so the initial bundle only needs the chunk for
// the page the visitor lands on. Antd-heavy pages (Contact's Form,
// Gallery's lightbox) no longer block the first paint of the home page.
const Home = lazyWithReload(() => import('../pages/Home'))
const Gallery = lazyWithReload(() => import('../pages/Gallery'))
const About = lazyWithReload(() => import('../pages/About'))
const Contact = lazyWithReload(() => import('../pages/Contact'))
const UsageGuide = lazyWithReload(() => import('../pages/UsageGuide'))
const NotFound = lazyWithReload(() => import('../pages/NotFound'))

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const lazyRoute = (Element: React.ComponentType) => (
  <Suspense fallback={<RouteFallback />}>
    <Element />
  </Suspense>
)

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: lazyRoute(Home) },
      { path: 'gallery', element: lazyRoute(Gallery) },
      { path: 'about', element: lazyRoute(About) },
      { path: 'contact', element: lazyRoute(Contact) },
      { path: 'docs/usage-guide', element: lazyRoute(UsageGuide) },
      { path: '*', element: lazyRoute(NotFound) },
    ],
  },
])
